// Copyright (c) 2023, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import CustomSnackbar from "../components/ui/Snackbars.js";
import IdleTimer from "../components/IdleTimer";
import LoadingButton from "@mui/lab/LoadingButton";
import MainLayout from "../components/MainLayout";
import NotFound from "./NotFound";
import UserContext from "../context/user-context";
import useHttp from "../utils/http";
import { Fragment } from "react";
import { useAuthContext } from "@asgardeo/auth-react";
import { BrowserRouter, Route, Switch, Redirect } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import {
  setIdToken,
  setUserName,
  getUserName,
  setUserRoles,
  setUserPrivileges,
  setRefreshTokenFunction,
  setSessionClearFunction,
  setEmployeeEmails,
  setAccessToken,
} from "../utils/oauth";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import {
  APP_NAME,
  APP_CONFIG,
  AUTH_CONFIG,
  APP_DESCRIPTION,
  QUERY_PARAMS_REGEX,
} from "../Config";
import { getEmployeeEmails, getPrivilegesByUser } from "../utils/end-points";
import { useHistory } from "react-router-dom";
import BackdropProgress from "../BackdropProgress.js";

import PRODUCT_LOGOS from "../images/Travel-app-login-logos.png";
import BACKGROUND_IMAGE from "../images/Travel-app-login-background.png";
import { LOGO_LIGHT_BG } from "../components/Logo";

const HomePage = () => {
  const history = useHistory();
  const {
    state,
    signIn,
    signOut,
    getBasicUserInfo,
    getIDToken,
    getDecodedIDToken,
    refreshAccessToken,
    getAccessToken,
    revokeAccessToken,
  } = useAuthContext();

  const [loggedOutStatus, setLoggedOutStatus] = useState("");
  const [loadApp, setLoadApp] = useState(false);
  const [loadPrivileges, setLoadPrivileges] = useState(false);
  const { handleRequest } = useHttp();

  //set backdrop for this
  const [isAppInitializing, setIsAppInitializing] = useState(false);
  const [snackbarData, setSnackbarData] = useState({
    open: false,
    message: "",
    severity: "",
    onClose: null,
  });

  const getIsInitLogin = () => {
    if (sessionStorage.getItem("isInitLogin") === "false") {
      return false;
    }
    return true;
  };

  const setIsLoggedOut = (value) => {
    sessionStorage.setItem("isLoggedOut", value);

    setLoggedOutState(value === "true");
  };

  const getIsLoggedOut = () => {
    if (sessionStorage.getItem("isLoggedOut") === "true") {
      return true;
    } else {
      return false;
    }
  };

  const [loggedOutState, setLoggedOutState] = useState(getIsLoggedOut());

  const setIsSessionTimeOut = (value) => {
    sessionStorage.setItem("isSessionTimeOut", value);
  };

  const getIsSessionTimeOut = () => {
    if (sessionStorage.getItem("isSessionTimeOut") === "true") {
      return true;
    } else {
      return false;
    }
  };

  const setPathNameToRedirect = () => {
    if (
      !sessionStorage.getItem("pathNameToRedirect") &&
      history &&
      history.location &&
      history.location.pathname &&
      history.location.pathname !== "/"
    ) {
      if (validatePathName(history.location.pathname)) {
        sessionStorage.setItem("pathNameToRedirect", history.location.pathname);
      }
      sessionStorage.setItem("pathNameToRedirect", history.location.pathname);
    }
  };

  const validatePathName = (pathname) => {
    let isValid = false;

    if (pathname !== "/") {
      if (pathname.split("/").length > 1) {
        let queryParams = pathname.split("/").pop();
        if (queryParams.length < 20 && QUERY_PARAMS_REGEX.test(queryParams)) {
          isValid = true;
        }
      } else {
        isValid = true;
      }
    }
    return isValid;
  };

  const getPathNameToRedirect = () => {
    return sessionStorage.getItem("pathNameToRedirect");
  };

  const redirectToPathName = () => {
    const pathNameToRedirect = getPathNameToRedirect();

    if (pathNameToRedirect != "null" && pathNameToRedirect) {
      sessionStorage.removeItem("pathNameToRedirect");
      history.push(pathNameToRedirect);
    }
  };

  const initUserPrivilegesAndEmails = useCallback(async (callbackFn) => {
    setIsAppInitializing(true);
    const promises = [];
    promises.push(
      new Promise(function (resolve, reject) {
        handleRequest(
          getPrivilegesByUser,
          "GET",
          null,
          (data) => {
            setUserPrivileges(data);
            resolve();
            if (data.length === 0) {
              setSnackbarData({
                message: "You are not privileged to view this app",
                open: true,
                severity: "error",
                onClose: onCloseSnackbar,
              });
              reject();
            } else if (callbackFn) {
              callbackFn();
            }
            setLoadPrivileges(true);
          },
          () => {
            setSnackbarData({
              message:
                "An error occurred in initializing the app! Try reloading the page. Please contact the Internal Apps Team if this issue continues.",
              open: true,
              severity: "error",
              onClose: onCloseSnackbar,
            });
            reject();
          }
        );
      })
    );

    promises.push(
      new Promise(function (resolve, reject) {
        handleRequest(
          getEmployeeEmails,
          "GET",
          null,
          (employees) => {
            if (employees) {
              setEmployeeEmails(employees);
              resolve();
            } else if (callbackFn) {
              callbackFn();
            } else {
              setSnackbarData({
                message: "Error while fetching employees",
                open: true,
                severity: "error",
                onClose: onCloseSnackbar,
              });
              reject();
            }
          },
          (error) => {
            setSnackbarData({
              message:
                "An error occurred in initializing the app! Try reloading the page. Please contact the Internal Apps Team if this issue continues.",
              open: true,
              severity: "error",
              onClose: onCloseSnackbar,
            });
            reject();
          }
        );
      })
    );

    Promise.all(promises)
      .then(() => {
        setIsAppInitializing(false);
      })
      .catch((error) => {
        console.error("Error while fetching privileges and emails");
      });
  }, []);

  const setIsInitLogin = (value) => {
    sessionStorage.setItem("isInitLogin", value);
  };

  const handleLogin = () => {
    setIsInitLogin("true");
    setIsSessionTimeOut("false");
    setIsLoggedOut("false");

    signIn().catch((e) => {
      console.error(e);
    });
  };

  const handleLogout = () => {
    setIsAppInitializing(true);
    setIsInitLogin("false");
    setIsLoggedOut("true");
    setIsSessionTimeOut("false");
    setIsAppInitializing(false);

    revokeAccessToken().then(signOut());
  };

  useEffect(() => {
    if (!getIsLoggedOut() && !(state?.isLoading || state?.isAuthenticated)) {
      handleLogin();
    }
  }, [state.isLoading, state.isAuthenticated]);

  const handleTokenRefresh = async () => {
    return refreshAccessToken()
      .then(async (e) => {
        setAccessToken(await getAccessToken());
      })
      .catch((err) => {
        if (err) {
          throw err;
        }
      });
  };

  useEffect(() => {
    setPathNameToRedirect();
  }, []);

  useEffect(() => {
    if (state && state.isAuthenticated) {
      setRefreshTokenFunction(handleTokenRefresh);
      setSessionClearFunction(sessionClearFn);

      const getData = async (callback) => {
        const basicUserInfo = await getBasicUserInfo();
        const accessToken = await getAccessToken();
        const idToken = await getIDToken();
        const decodedIDToken = await getDecodedIDToken();

        const authState = {
          authenticateResponse: basicUserInfo,
          idToken: idToken.split("."),
          decodedIdTokenHeader: JSON.parse(atob(idToken.split(".")[0])),
          decodedIDTokenPayload: decodedIDToken,
        };
        setIdToken(idToken);
        setAccessToken(accessToken);

        if (accessToken) {
          setLoadApp(true);
          callback?.();
        }
        if (basicUserInfo?.email) {
          setUserName(basicUserInfo.email);
        }
        if (basicUserInfo?.groups) {
          setUserRoles(basicUserInfo.groups);
        }
      };
      getData(() => {
        initUserPrivilegesAndEmails(redirectToPathName());
      });
    }
  }, [state.isAuthenticated, state.isLoading]);

  const onCloseSnackbar = () => {
    setSnackbarData({ ...snackbarData, open: false });
  };

  const sessionClearFn = () => {
    setLoadApp(false);
    setIsInitLogin("false");
    setIsSessionTimeOut("true");
    setIsLoggedOut("true");

    revokeAccessToken().catch((e) => {
      console.error(e);
    });
    history.push("");
  };

  return (
    <Fragment>
      <CustomSnackbar snackbar={snackbarData} />
      <BackdropProgress open={isAppInitializing} />

      {AUTH_CONFIG.clientID === "" ? (
        <div className="content">
          <h2>You need to update the Client ID to proceed.</h2>
          <p>
            Please open "src/config.json" file using an editor, and update the{" "}
            <code>clientID</code> value with the registered application's client
            ID.
          </p>
          <p>
            Visit repo{" "}
            <a href="https://github.com/asgardeo/asgardeo-auth-react-sdk/tree/master/samples/asgardeo-react-app">
              README
            </a>{" "}
            for more details.
          </p>
        </div>
      ) : state.isAuthenticated && loadApp && loadPrivileges ? (
        <UserContext.Provider
          value={{
            userName: getUserName(),
            handleLogout: handleLogout,
          }}
        >
          <BrowserRouter>
            <Switch>
              <Route
                path={APP_CONFIG.PAGES.APP}
                render={({ match, location, history }) => {
                  return <MainLayout page={location.pathname} />;
                }}
              />
              <Redirect exact from="/" to={APP_CONFIG.PAGES.APP} />
              <Route component={NotFound} />
            </Switch>
            <IdleTimer sessionClearFn={sessionClearFn} />
          </BrowserRouter>
        </UserContext.Provider>
      ) : (
        <>
          <title>Login | {APP_NAME}</title>

          <Box
            sx={{
              backgroundColor: "background.default",
              display: "flex",
              flexDirection: "column",
              height: "100%",
              justifyContent: "center",
              backgroundImage: `url(${BACKGROUND_IMAGE})`,
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center",
            }}
          >
            <Container fixed maxWidth="xs">
              <Card
                elevation={24}
                sx={{
                  borderRadius: 3,
                  pt: 3,
                  mx: 2,
                }}
              >
                <CardContent>
                  <Box
                    sx={{
                      px: 3,
                    }}
                  >
                    <Grid
                      container
                      direction="column"
                      justifyContent="center"
                      alignItems="center"
                      spacing={2}
                      p={1}
                    >
                      <Grid item xs={12}>
                        <img
                          alt="logo"
                          width="130"
                          height="auto"
                          src={LOGO_LIGHT_BG}
                        ></img>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography sx={{ fontWeight: "bold" }} variant="h1">
                          {APP_NAME}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sx={{ pb: 2 }}>
                        <Typography align="center" sx={{ fontSize: "1em" }}>
                          {APP_DESCRIPTION}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <LoadingButton
                          id="login"
                          onClick={() => {
                            handleLogin();
                          }}
                          variant="contained"
                          color="secondary"
                          loading={getIsInitLogin()}
                          loadingPosition="center"
                        >
                          Log In
                        </LoadingButton>
                      </Grid>
                      {getIsInitLogin() && (
                        <Grid item xs={12}>
                          <Typography variant="caption">
                            Redirecting to Asgardeo...
                          </Typography>
                        </Grid>
                      )}
                      <Grid item xs={12} mt={10}>
                        <Stack direction="column" spacing={2}>
                          <Typography align="center">Powered By</Typography>

                          <Stack direction="row" spacing={2}>
                            <img height={22} src={PRODUCT_LOGOS} />
                          </Stack>
                        </Stack>
                      </Grid>
                      <Grid item xs={12} mt={3}>
                        <Typography
                          align="center"
                          color="text.secondary"
                          sx={{ fontSize: "0.8em" }}
                        >
                          {"© 2023 WSO2 LLC"}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                </CardContent>
                <Divider />
              </Card>
            </Container>
          </Box>
        </>
      )}
    </Fragment>
  );
};

export default HomePage;
